import React, { useEffect, useRef, useState, useCallback } from "react";
import "./CustomColorPicker.scss";
import CustomColorEditIcon from "../../../components/icons/CustomColorEditIcon";

/**
 * @typedef {Object} RGB
 * @property {number} r
 * @property {number} g
 * @property {number} b
 */

/**
 * @typedef {Object} HSV
 * @property {number} h
 * @property {number} s
 * @property {number} v
 */

/**
 * @typedef {Object} CustomColorPickerProps
 * @property {string} [initialHex]
 * @property {(hex: string, rgb: RGB) => void} onDone
 * @property {() => void} onClose
 */

/* ---------- helpers ---------- */

/**
 * @param {number} h
 * @param {number} s
 * @param {number} v
 * @returns {RGB}
 */
const hsvToRgb = (h, s, v) => {
  s /= 100;
  v /= 100;
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0,
    g = 0,
    b = 0;

  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }

  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
};

/**
 * @param {number} r
 * @param {number} g
 * @param {number} b
 * @returns {HSV}
 */
const rgbToHsv = (r, g, b) => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  let h = 0;

  if (delta !== 0) {
    if (max === r) h = 60 * (((g - b) / delta) % 6);
    else if (max === g) h = 60 * ((b - r) / delta + 2);
    else h = 60 * ((r - g) / delta + 4);
  }
  if (h < 0) h += 360;

  const s = max === 0 ? 0 : delta / max;
  return { h, s: s * 100, v: max * 100 };
};

/**
 * @param {number} r
 * @param {number} g
 * @param {number} b
 * @returns {string}
 */
const rgbToHex = (r, g, b) =>
  (
    "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")
  ).toUpperCase();

/**
 * @param {string} hex
 * @returns {RGB | null}
 */
const hexToRgb = (hex) => {
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3)
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
};

/**
 * Custom Color Picker Component (mobile-friendly)
 * @param {CustomColorPickerProps} props
 */
const CustomColorPicker = ({ initialHex = "#5A5A5A", onDone, onClose }) => {
  const colorAreaRef = useRef(null);
  const hueSliderRef = useRef(null);

  const initRgb = hexToRgb(initialHex) || { r: 90, g: 90, b: 90 };
  const initHsv = rgbToHsv(initRgb.r, initRgb.g, initRgb.b);

  const [hue, setHue] = useState(initHsv.h);
  const [saturation, setSaturation] = useState(initHsv.s);
  const [value, setValue] = useState(initHsv.v);
  const [format, setFormat] = useState("rgb"); // "rgb" | "hex"
  const [hexInput, setHexInput] = useState(initialHex.toUpperCase());

  const rgb = hsvToRgb(hue, saturation, value);
  const currentHex = rgbToHex(rgb.r, rgb.g, rgb.b);

  useEffect(() => {
    setHexInput(currentHex);
  }, [currentHex]);

  /* ---------- color area drag ---------- */

  const updateFromArea = useCallback((clientX, clientY) => {
    const el = colorAreaRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    const y = Math.max(0, Math.min(rect.height, clientY - rect.top));
    setSaturation((x / rect.width) * 100);
    setValue(100 - (y / rect.height) * 100);
  }, []);

  const handleAreaPointerDown = (e) => {
    e.target.setPointerCapture(e.pointerId);
    updateFromArea(e.clientX, e.clientY);
  };

  const handleAreaPointerMove = (e) => {
    if ((e.buttons & 1) === 1) updateFromArea(e.clientX, e.clientY);
  };

  /* ---------- hue drag ---------- */

  const updateFromHue = useCallback((clientX) => {
    const el = hueSliderRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    setHue((x / rect.width) * 360);
  }, []);

  const handleHuePointerDown = (e) => {
    e.target.setPointerCapture(e.pointerId);
    updateFromHue(e.clientX);
  };

  const handleHuePointerMove = (e) => {
    if ((e.buttons & 1) === 1) updateFromHue(e.clientX);
  };

  /* ---------- RGB inputs ---------- */

  /**
   * @param {"r" | "g" | "b"} channel
   * @param {string} val
   */
  const handleRgbChange = (channel, val) => {
    let num = parseInt(val, 10);
    if (Number.isNaN(num)) num = 0;
    num = Math.max(0, Math.min(255, num));
    const newRgb = { ...rgb, [channel]: num };
    const hsv = rgbToHsv(newRgb.r, newRgb.g, newRgb.b);
    setHue(hsv.h);
    setSaturation(hsv.s);
    setValue(hsv.v);
  };

  /* ---------- HEX input ---------- */

  /**
   * @param {string} val
   */
  const handleHexChange = (val) => {
    let v = val.toUpperCase();
    if (!v.startsWith("#")) v = "#" + v;
    setHexInput(v);
    const parsed = hexToRgb(v);
    if (parsed) {
      const hsv = rgbToHsv(parsed.r, parsed.g, parsed.b);
      setHue(hsv.h);
      setSaturation(hsv.s);
      setValue(hsv.v);
    }
  };

  /* ---------- eyedropper ---------- */

  const handleEyedropper = async () => {
    if (!window.EyeDropper) return;
    try {
      const ed = new window.EyeDropper();
      const result = await ed.open();
      const parsed = hexToRgb(result.sRGBHex);
      if (!parsed) return;
      const hsv = rgbToHsv(parsed.r, parsed.g, parsed.b);
      setHue(hsv.h);
      setSaturation(hsv.s);
      setValue(hsv.v);
    } catch {
      /* user cancelled */
    }
  };

  useEffect(() => {
    if (onDone) onDone(currentHex, rgb);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentHex]);

  const handleDone = () => {
    onClose();
  };

  const areaBg = `linear-gradient(to bottom, transparent 0%, #000 100%),
    linear-gradient(to right, #fff 0%, hsl(${hue}, 100%, 50%) 100%)`;

  return (
    <div className="custom-color-picker" onClick={(e) => e.stopPropagation()}>
      <div
        ref={colorAreaRef}
        className="ccp-color-area"
        style={{ background: areaBg }}
        onPointerDown={handleAreaPointerDown}
        onPointerMove={handleAreaPointerMove}
      >
        <div
          className="ccp-color-cursor"
          style={{
            left: `${saturation}%`,
            top: `${100 - value}%`,
          }}
        />
      </div>

      <div className="ccp-hue-row">
        <div className="ccp-hue-circle" style={{ background: currentHex }} />

        <div
          ref={hueSliderRef}
          className="ccp-hue-slider"
          onPointerDown={handleHuePointerDown}
          onPointerMove={handleHuePointerMove}
        >
          <div
            className="ccp-hue-cursor"
            style={{
              left: `${(hue / 360) * 100}%`,
              background: `hsl(${hue}, 100%, 50%)`,
            }}
          />
        </div>
      </div>

      {/* Bottom row */}
      <div className="ccp-bottom-row">
        {/* Eyedropper pill */}
        <button
          type="button"
          className="ccp-eyedropper"
          onClick={handleEyedropper}
          title="Pick color"
        >
          <CustomColorEditIcon />
        </button>

        {/* Format toggle pill */}
        <div
          className="ccp-format-toggle"
          onClick={() => setFormat(format === "rgb" ? "hex" : "rgb")}
        >
          <span className="ccp-format-label">{format.toUpperCase()}</span>
          <svg width="9" height="6" viewBox="0 0 9 6" fill="none">
            <path
              d="M1 1L4.5 4.5L8 1"
              stroke="#111"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* Value inputs */}
        {format === "rgb" ? (
          <div className="ccp-rgb-inputs">
            <input
              type="number"
              min={0}
              max={255}
              value={rgb.r}
              onChange={(e) => handleRgbChange("r", e.target.value)}
              className="ccp-rgb-input"
            />
            <input
              type="number"
              min={0}
              max={255}
              value={rgb.g}
              onChange={(e) => handleRgbChange("g", e.target.value)}
              className="ccp-rgb-input"
            />
            <input
              type="number"
              min={0}
              max={255}
              value={rgb.b}
              onChange={(e) => handleRgbChange("b", e.target.value)}
              className="ccp-rgb-input"
            />
          </div>
        ) : (
          <input
            type="text"
            value={hexInput}
            onChange={(e) => handleHexChange(e.target.value)}
            maxLength={7}
            className="ccp-hex-input"
            spellCheck={false}
          />
        )}

        {/* Done button */}
        <button type="button" className="ccp-done-btn" onClick={handleDone}>
          Done
        </button>
      </div>
    </div>
  );
};

export default CustomColorPicker;
